import { useEffect, useState } from 'react';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { AccountSearchField } from './AccountSearchField';
import { visibleEntries } from './accountSwitcherLabels';
import { AccountSwitcherRow } from './AccountSwitcherRow';
import { useSwitcherEntries } from './useSwitcherEntries';

const customStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '340px',
    maxWidth: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    flex: 1,
    minWidth: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '12px 0 0',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '340px',
    overflowY: 'auto',
  },
  empty: {
    fontSize: '13px',
    color: '#6E7178',
    padding: '8px 10px',
  },
} satisfies Record<string, SxProps<Theme>>;

interface Props {
  /** True while this panel is the visible face of the user menu. */
  open: boolean;
  /** The account currently being switched to, from `useAccountSwitch`. */
  switchingTo: number | null;
  onSwitch: (userId: number, accountName: string) => void;
  /** Returns to the menu items, in the same popover. */
  onBack: () => void;
  onClose: () => void;
}

/**
 * The complete list of accounts, as a second face of the user menu.
 *
 * It lives inside the popover rather than in a dialog over it, which is what the
 * design asks for: pressing "see all accounts" turns the menu into this list
 * instead of stacking a window on top of it. That is safe for the switch it can
 * start because the popover deliberately stays open while a switch is in flight,
 * so the row spinner is not unmounted under it.
 *
 * The current account is pinned first here too, with the rest alphabetical.
 * That is what the design shows: in the reference the current account sits above
 * an account whose name sorts before it, so the pin is not a menu-only device
 * for the row cut. It also keeps the answer to "where am I?" at the top of a
 * list that can run to hundreds of rows.
 *
 * The search resets on open so a term typed on a previous visit does not come
 * back as an unexplained near-empty list.
 */
export function AllAccountsPanel({ open, switchingTo, onSwitch, onBack, onClose }: Props) {
  const lang = useLanguage();
  const { entries, currentUserId, total } = useSwitcherEntries();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const copy = lang.mainTopBar.accountSwitcher;

  if (!open) return null;

  const rows = visibleEntries(entries, search, { pinnedUserId: currentUserId });

  return (
    <Box
      sx={customStyles.root}
      data-testid='all-accounts-panel'
    >
      <Box sx={customStyles.header}>
        <IconButton
          onClick={onBack}
          size='small'
          aria-label={copy.back}
          data-testid='all-accounts-back'
        >
          <ArrowBackRoundedIcon fontSize='small' />
        </IconButton>
        <Typography sx={customStyles.title}>
          {copy.seeAllAccounts.replace('{{count}}', String(total))}
        </Typography>
        <IconButton
          onClick={onClose}
          size='small'
          aria-label={copy.close}
          data-testid='all-accounts-close'
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
      <Box sx={customStyles.body}>
        <AccountSearchField
          value={search}
          onChange={setSearch}
          placeholder={copy.searchPlaceholder}
          buttonLabel={copy.searchAction}
          testId='all-accounts-search'
        />
        <Box sx={customStyles.list}>
          {rows.length === 0 && (
            <Typography
              sx={customStyles.empty}
              data-testid='all-accounts-no-results'
            >
              {copy.noResults}
            </Typography>
          )}
          {rows.map((entry) => (
            <AccountSwitcherRow
              key={entry.user_id}
              entry={entry}
              isCurrent={entry.user_id === currentUserId}
              isSwitching={switchingTo === entry.user_id}
              currentAccountLabel={copy.currentAccount}
              avatarSize={32}
              onSelect={onSwitch}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
